import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { pageKeyForPath, pageIsVisible } from "./lib/pageVisibility";
import Layout from "./components/Layout";
import UpdateBanner from "./components/UpdateBanner";
import ConnectionBanner from "./components/ConnectionBanner";

// Note: we intentionally do NOT set axios.defaults.withCredentials = true.
// In production the frontend (cll-fieldready.com) and API (cll-fieldready.com/api)
// are same-origin, so cookies are sent automatically by the browser. Enabling
// `withCredentials` was previously thought to help Cloudflare bot protection,
// but in practice it triggered Safari's strict-cookie / ITP path and made the
// app unreachable in Private Browsing and immediately after a "Manage Website
// Data → Remove" reset, because Safari rejects credentialed responses that
// echo back `Access-Control-Allow-Origin: *` (which our backend does in some
// CORS replies). Leaving cookies to the browser default fixes both scenarios
// without any production downside.

// Pages
import Login from "./pages/Login";
import MagicLogin from "./pages/MagicLogin";
import AcceptInvite from "./pages/AcceptInvite";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetNewPassword from "./pages/SetNewPassword";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import CalendarPage from "./pages/CalendarPage";
import MyJobs from "./pages/MyJobs";
import Timesheet from "./pages/Timesheet";
import TimesheetWall from "./pages/TimesheetWall";
import TimesheetApproval from "./pages/TimesheetApproval";
import HoursByJob from "./pages/HoursByJob";
import PayrollReconciliation from "./pages/PayrollReconciliation";
import Reports from "./pages/Reports";
import ReportForms from "./pages/ReportForms";
import FieldReports from "./pages/FieldReports";
import Team from "./pages/Team";
import Contacts from "./pages/Contacts";
import PeopleContacts from "./pages/PeopleContacts";
import Opportunities from "./pages/Opportunities";
import Services from "./pages/Services";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Permissions from "./pages/Permissions";
import StorageStats from "./pages/StorageStats";
import Profile from "./pages/Profile";
import Proposals from "./pages/Proposals";
import ProposalPreview from "./pages/ProposalPreview";
import ProposalSandbox from "./pages/ProposalSandbox";
import JobAudit from "./pages/JobAudit";
import AdminTrash from "./pages/AdminTrash";
import AdminEquipmentRecovery from "./pages/AdminEquipmentRecovery";
import AdminPhotoAudit from "./pages/AdminPhotoAudit";
import AdminPhotoAuditRecover from "./pages/AdminPhotoAuditRecover";
import AdminPhotoHistory from "./pages/AdminPhotoHistory";
import AdminSignaturePurge from "./pages/AdminSignaturePurge";
import AdminToolsPage from "./pages/AdminToolsPage";
import SecurityTestResultsPage from "./pages/SecurityTestResults";
import Maintenance from "./pages/Maintenance";
import Archive from "./pages/Archive";
import ExpenseReports from "./pages/ExpenseReports";
import OfficeDashboard from "./pages/OfficeDashboard";
import RecurringJobs from "./pages/RecurringJobs";
import Tickets811 from "./pages/Tickets811";
import SoftDigForm from "./pages/SoftDigForm";
import PreConForm from "./pages/PreConForm";
import CSEForm from "./pages/CSEForm";
import LocateForm from "./pages/LocateForm";
import ReviewQueue from "./pages/ReviewQueue";
import FieldTimeTrackerV2 from "./pages/FieldTimeTrackerV2";
import MyDayTimeline from "./pages/MyDayTimeline";
import ApprovedReports from "./pages/ApprovedReports";
import AssetDetail from "./pages/AssetDetail";
import EquipmentOverview from "./pages/EquipmentOverview";
import Feedback from "./pages/Feedback";
import Reminders from "./pages/Reminders";
import Certifications from "./pages/Certifications";
import MyCertifications from "./pages/MyCertifications";
import TimesheetPreview from "./pages/TimesheetPreview";
import ExpenseReportPreview from "./pages/ExpenseReportPreview";
import SoftDigPreview from "./pages/SoftDigPreview";
import LocatePreview from "./pages/LocatePreview";
import PreConPreview from "./pages/PreConPreview";
import CSEPreview from "./pages/CSEPreview";
import MaintenancePreview from "./pages/MaintenancePreview";
import TimesheetMemberPreview from "./pages/TimesheetMemberPreview";
import TimesheetTeamPreview from "./pages/TimesheetTeamPreview";
import Styleguide from "./pages/Styleguide";

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to set-new-password if user must change password (except if already on that page)
  if (mustChangePassword && location.pathname !== '/set-new-password') {
    return <Navigate to="/set-new-password" replace />;
  }

  // Per-user page visibility — block deep links the sidebar item is hidden for.
  // Admins / super_users always pass. Dashboard cannot be hidden (would loop).
  const pageKey = pageKeyForPath(location.pathname);
  if (pageKey && pageKey !== 'dashboard' && !pageIsVisible(user, pageKey)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Protected Route WITHOUT Layout (for print pages)
function ProtectedRouteNoLayout({ children }) {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to set-new-password if user must change password (except if already on that page)
  if (mustChangePassword && location.pathname !== '/set-new-password') {
    return <Navigate to="/set-new-password" replace />;
  }
  
  return children;
}

// Public Route wrapper (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/magic-login" element={<MagicLogin />} />
      <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/set-new-password" element={<ProtectedRouteNoLayout><SetNewPassword /></ProtectedRouteNoLayout>} />
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/my-jobs" element={<ProtectedRoute><MyJobs /></ProtectedRoute>} />
      <Route path="/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
      <Route path="/timesheet/approval" element={<ProtectedRoute><TimesheetApproval /></ProtectedRoute>} />
      <Route path="/timesheet/hours-by-job" element={<ProtectedRoute><HoursByJob /></ProtectedRoute>} />
      <Route path="/timesheet/wall" element={<ProtectedRouteNoLayout><TimesheetWall /></ProtectedRouteNoLayout>} />
      <Route path="/timesheet/:id/preview" element={<ProtectedRouteNoLayout><TimesheetPreview /></ProtectedRouteNoLayout>} />
      <Route path="/timesheet/member/:userId/week/:weekStart/preview" element={<ProtectedRouteNoLayout><TimesheetMemberPreview /></ProtectedRouteNoLayout>} />
      <Route path="/timesheet/team/:weekStart/preview" element={<ProtectedRouteNoLayout><TimesheetTeamPreview /></ProtectedRouteNoLayout>} />
      <Route path="/payroll-reconciliation" element={<ProtectedRoute><PayrollReconciliation /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/report-forms" element={<ProtectedRoute><ReportForms /></ProtectedRoute>} />
      <Route path="/field-reports" element={<ProtectedRoute><FieldReports /></ProtectedRoute>} />
      <Route path="/soft-dig/:id/preview" element={<ProtectedRouteNoLayout><SoftDigPreview /></ProtectedRouteNoLayout>} />
      <Route path="/locate/:id/preview" element={<ProtectedRouteNoLayout><LocatePreview /></ProtectedRouteNoLayout>} />
      <Route path="/precon/:id/preview" element={<ProtectedRouteNoLayout><PreConPreview /></ProtectedRouteNoLayout>} />
      <Route path="/confined-space/:id/preview" element={<ProtectedRouteNoLayout><CSEPreview /></ProtectedRouteNoLayout>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/people" element={<ProtectedRoute><PeopleContacts /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/contacts" element={<Navigate to="/people" replace />} />
      <Route path="/opportunities" element={<ProtectedRoute><Opportunities /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
      <Route path="/admin/storage" element={<ProtectedRoute><StorageStats /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
      <Route path="/proposals/:id/preview" element={<ProtectedRouteNoLayout><ProposalPreview /></ProtectedRouteNoLayout>} />
      <Route path="/proposal-sandbox" element={<ProtectedRouteNoLayout><ProposalSandbox /></ProtectedRouteNoLayout>} />
      <Route path="/admin/job-audit" element={<ProtectedRoute><JobAudit /></ProtectedRoute>} />
      <Route path="/admin/trash" element={<ProtectedRoute><AdminTrash /></ProtectedRoute>} />
      <Route path="/admin/equipment-recovery" element={<ProtectedRoute><AdminEquipmentRecovery /></ProtectedRoute>} />
      <Route path="/admin/photo-audit" element={<ProtectedRoute><AdminPhotoAudit /></ProtectedRoute>} />
      <Route path="/admin/photo-audit/recover/:formType/:formId" element={<ProtectedRoute><AdminPhotoAuditRecover /></ProtectedRoute>} />
      <Route path="/admin/photo-history" element={<ProtectedRoute><AdminPhotoHistory /></ProtectedRoute>} />
      <Route path="/admin/signature-purge" element={<ProtectedRoute><AdminSignaturePurge /></ProtectedRoute>} />
      <Route path="/admin/tools" element={<ProtectedRoute><AdminToolsPage /></ProtectedRoute>} />
      <Route path="/admin/security-tests" element={<ProtectedRoute><SecurityTestResultsPage /></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><Maintenance /></ProtectedRoute>} />
      <Route path="/maintenance/:id/preview" element={<ProtectedRouteNoLayout><MaintenancePreview /></ProtectedRouteNoLayout>} />
      <Route path="/archive" element={<ProtectedRoute><Archive /></ProtectedRoute>} />
      <Route path="/expense-reports" element={<ProtectedRoute><ExpenseReports /></ProtectedRoute>} />
      <Route path="/expense-report/:id/preview" element={<ProtectedRouteNoLayout><ExpenseReportPreview /></ProtectedRouteNoLayout>} />
      <Route path="/office" element={<ProtectedRoute><OfficeDashboard /></ProtectedRoute>} />
      <Route path="/recurring-jobs" element={<ProtectedRoute><RecurringJobs /></ProtectedRoute>} />
      <Route path="/811-info" element={<ProtectedRoute><Tickets811 /></ProtectedRoute>} />
      <Route path="/soft-dig" element={<ProtectedRoute><SoftDigForm /></ProtectedRoute>} />
      <Route path="/soft-dig/:id" element={<ProtectedRoute><SoftDigForm /></ProtectedRoute>} />
      <Route path="/precon" element={<ProtectedRoute><PreConForm /></ProtectedRoute>} />
      <Route path="/precon/:id" element={<ProtectedRoute><PreConForm /></ProtectedRoute>} />
      <Route path="/confined-space" element={<ProtectedRoute><CSEForm /></ProtectedRoute>} />
      <Route path="/confined-space/:id" element={<ProtectedRoute><CSEForm /></ProtectedRoute>} />
      <Route path="/locate" element={<ProtectedRoute><LocateForm /></ProtectedRoute>} />
      <Route path="/locate/:id" element={<ProtectedRoute><LocateForm /></ProtectedRoute>} />
      <Route path="/review-queue" element={<ProtectedRoute><ReviewQueue /></ProtectedRoute>} />
      <Route path="/field-tracker-v2" element={<ProtectedRoute><FieldTimeTrackerV2 /></ProtectedRoute>} />
      <Route path="/field/my-day" element={<ProtectedRoute><MyDayTimeline /></ProtectedRoute>} />
      <Route path="/approved-reports" element={<ProtectedRoute><ApprovedReports /></ProtectedRoute>} />
      <Route path="/asset/:assetId" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />
      <Route path="/equipment-overview" element={<ProtectedRoute><EquipmentOverview /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
      <Route path="/certifications" element={<ProtectedRoute><Certifications /></ProtectedRoute>} />
      <Route path="/my-certifications" element={<ProtectedRoute><MyCertifications /></ProtectedRoute>} />
      <Route path="/styleguide" element={<ProtectedRoute><Styleguide /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          offset="calc(env(safe-area-inset-top, 0px) + 16px)"
          mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', right: '12px', left: '12px' }}
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif'
            }
          }}
        />
        <UpdateBanner />
        <ConnectionBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
